// The pure rules behind a task's MEDIA (ART-368). The slot table mirrors `TaskMediaSlots` server-side, which is
// authoritative and refuses anything else; the client uses it to draw only the slots that fit an item.
import { formatFileSize } from "../../app/hooks/inboxFormat.js";
import type { TaskMediaDto } from "../../app/hooks/useTaskMedia.js";

export type MediaSlot = "cover" | "canvas" | "music_video" | "lyric_video" | "file";

/** The Assets tab's own order, which the push picker follows. */
export const SLOT_ORDER: readonly MediaSlot[] = ["cover", "canvas", "music_video", "lyric_video", "file"];

export const SLOT_LABELS: Record<MediaSlot, string> = {
  cover: "Cover artwork",
  canvas: "Spotify Canvas",
  music_video: "Music video",
  lyric_video: "Lyric video",
  file: "Files",
};

/** Where an upload of this media kind can go. Music and lyric video take links on the release today. */
export function slotsForMedia(kind: string | null | undefined): MediaSlot[] {
  if (kind === "image") return ["cover", "canvas", "file"];
  if (kind === "video") return ["canvas", "file"];
  return ["file"];
}

/** Where a link can go. */
export const LINK_SLOTS: readonly MediaSlot[] = ["music_video", "lyric_video"];

/** A finished task — Done, or Approved (the reviewer's sign-off) — is what a push needs. */
export function allowsPush(status: string | null | undefined): boolean {
  return status === "done" || status === "approved";
}

/** The tile's meta line: dimensions for an image, length for a video or audio file, size otherwise. */
export function mediaMeta(m: Pick<TaskMediaDto, "kind" | "width" | "height" | "durationMs" | "sizeBytes">): string {
  if (m.kind === "image" && m.width && m.height) return `${m.width} × ${m.height}`;
  if ((m.kind === "video" || m.kind === "audio") && m.durationMs) return formatDuration(m.durationMs);
  return m.sizeBytes ? formatFileSize(m.sizeBytes) : "";
}

function formatDuration(ms: number): string {
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** The type tile for a file we don't draw a picture of: its extension ("PDF", "MP4", "WAV"), else its kind. */
export function typeLabel(fileName: string | null | undefined, kind: string | null | undefined): string {
  const ext = fileName?.includes(".") ? fileName.split(".").pop() : null;
  if (ext && ext.length <= 5) return ext.toUpperCase();
  return (kind ?? "file").toUpperCase();
}
